/* eslint-disable max-lines-per-function */
import React from "react";
import { createBrowserRouter, RouterProvider, createRoutesFromElements, Route, Navigate, useLocation, useNavigate, Outlet } from "react-router-dom";
import { ApolloProvider } from "@apollo/client/react";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ThemeProvider } from "@mui/material/styles";
import { PostHogProvider } from "posthog-js/react";
import { SnackbarProvider } from "components/haven-design/snackbar/context";
import { useSnackbar } from "components/haven-design/snackbar";
import ViewerProvider from "components/viewer/provider";
import { registerNavigate } from "utils/navigation";

import theme from "components/theme";
import { TimeoutLogoutComponent } from "components/timeout-logout-component";
import NonProductionBanner from "components/non-production-banner";

import { LoginPage } from "components/login";
import { TablesRefProvider } from "components/table-refs";
import { apolloClient } from "data/graphql";

import { EmptyLayout, MainLayout } from "pages/layouts";

import NotFound from "pages/not-found";
import NotAuthorized from "pages/not-authorized";

import Home from "pages/home/home";
import About from "pages/static/about";
import Support from "pages/support/support";
import Demo from "pages/demo";
import DemoThanks from "pages/demo/demo-thanks";
import Terms from "pages/static/terms";

import JobsPage from "pages/jobs";
import FullStackEng from "pages/jobs/engineering-full-stack";
import AccountExec from "pages/jobs/account-exec";
import ProductManager from "pages/jobs/product-manager";
import ProductDesigner from "pages/jobs/product-designer";
import HeadOfSales from "pages/jobs/head-sales";

import LinkApp from "pages/link-app";

import UpdateForm from "pages/waitlist-update/update-form/components";

import Logout from "pages/logout";
import ResetPassword from "pages/login/reset-password";
import CheckEmail from "pages/login/check-email";
import ChoosePassword from "pages/login/choose-password";
import { NewUser } from "pages/login/new-user";
import { NewUserUpdate } from "pages/login/new-user-update";
import NoAccount from "pages/login/no-account";
import EmailVerified from "pages/login/verified";
import VerifyEmail from "pages/login/verify-email";

import EligibilityLayoutV2 from "pages/eligibility/layout";
import EligibilityFormV2 from "pages/eligibility";
import FormCompleted from "pages/eligibility/components/form-completed";

import FormLayout from "pages/form";

import ClientDashboard from "pages/dashboard/clients";
import AdminDashboard from "pages/dashboard/admin";
import ManagerDashboard from "pages/dashboard/manager";

import Properties from "pages/properties";


const ScrollToTopOfPage: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const interestsListRegex = /^\/dash\/manager\/lists\/[\w-]+\/p\//;
  const interestSlideoutRegex = /^\/dash\/manager\/lists\/[\w-]+\/p\/\d\/interests\/[\w-]+/;
  const location = useLocation();

  React.useEffect(() => {
    if (window.location.pathname.match(interestSlideoutRegex)) return;
    if (window.location.pathname.match(interestsListRegex)) return;
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return <>{children}</>;
};

// No longer declare layout route trees here; we compose per-route below

const postHogOptions = {
  // eslint-disable-next-line camelcase
  api_host: process.env.PUBLIC_POSTHOG_HOST ?? "",
};

const NavigateRegistrar: React.FC = () => {
  const navigate = useNavigate();
  React.useEffect(() => {
    registerNavigate((to, options) => navigate(to, options));
  }, [navigate]);
  return null;
};

const RouterShell: React.FC = () => {
  const { openSnackbar } = useSnackbar();
  const location = useLocation();

  React.useEffect(() => {
    try {
      const raw = sessionStorage.getItem("global.snackbar");
      if (raw) {
        sessionStorage.removeItem("global.snackbar");
        const payload = JSON.parse(raw);
        openSnackbar(payload);
      }
    } catch {}
  }, [location.key]);

  return (
    <>
      <NavigateRegistrar />
      <ScrollToTopOfPage>
        <Outlet />
      </ScrollToTopOfPage>
    </>
  );
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RouterShell />}>
      {/* Dashboards — split multi-segment splat so descendant <Routes> client-nav
          works under React Router 7 (see v7_relativeSplatPath upgrade).
          Index routes are required: `*` matches a trailing segment, not the bare path. */}
      <Route path="/dash/client">
        <Route index element={<ClientDashboard />} />
        <Route path="*" element={<ClientDashboard />} />
      </Route>
      <Route path="/dash/admin">
        <Route index element={<AdminDashboard />} />
        <Route path="*" element={<AdminDashboard />} />
      </Route>
      <Route path="/dash/manager">
        <Route index element={<Navigate to="/dash/manager/lists" replace />} />
        <Route path="*" element={<ManagerDashboard />} />
      </Route>

      {/* Update and eligibility */}
      <Route path="/update/:code" element={<UpdateForm />} />
      <Route path="/application/*" element={<EligibilityLayoutV2 />}>
        <Route path=":submissionId/*" element={<EligibilityFormV2 />} />
      </Route>
      <Route path="/form-completed" element={<FormCompleted />} />
      <Route path="/form/:formId" element={<FormLayout />} />
      <Route path="/signup/:formId" element={<FormLayout />} />

      {/* Marketing/Main layout routes rendered per path */}
      <Route path="/" element={<MainLayout showHeader showFooter backgroundColor="white"><Home /></MainLayout>} />
      <Route path="/properties" element={<MainLayout showHeader showFooter backgroundColor="white"><Properties /></MainLayout>} />
      <Route path="/about" element={<MainLayout showHeader showFooter backgroundColor="white"><About /></MainLayout>} />
      <Route path="/terms" element={<MainLayout showHeader showFooter backgroundColor="white"><Terms /></MainLayout>} />
      <Route path="/support/*" element={<MainLayout showHeader showFooter backgroundColor="white"><Support /></MainLayout>} />
      <Route path="/demo" element={<MainLayout showHeader showFooter backgroundColor="white"><Demo /></MainLayout>} />
      <Route path="/demo-thanks" element={<MainLayout showHeader showFooter backgroundColor="white"><DemoThanks /></MainLayout>} />
      <Route path="/not-authorized" element={<MainLayout showHeader showFooter backgroundColor="white"><NotAuthorized /></MainLayout>} />
      <Route path="/login" element={<MainLayout showHeader showFooter backgroundColor="white"><LoginPage /></MainLayout>} />
      <Route path="/signup" element={<MainLayout showHeader showFooter backgroundColor="white"><LoginPage isSignup /></MainLayout>} />
      <Route path="/reset-password" element={<MainLayout showHeader showFooter backgroundColor="white"><ResetPassword /></MainLayout>} />

      {/* Careers */}
      <Route path="/careers" element={<MainLayout showHeader showFooter backgroundColor="white"><JobsPage /></MainLayout>} />
      <Route path="/careers/full-stack-engineer" element={<MainLayout showHeader showFooter backgroundColor="white"><FullStackEng /></MainLayout>} />
      <Route path="/careers/account-exec" element={<MainLayout showHeader showFooter backgroundColor="white"><AccountExec /></MainLayout>} />
      <Route path="/careers/product-manager" element={<MainLayout showHeader showFooter backgroundColor="white"><ProductManager /></MainLayout>} />
      <Route path="/careers/product-designer" element={<MainLayout showHeader showFooter backgroundColor="white"><ProductDesigner /></MainLayout>} />
      <Route path="/careers/head-sales" element={<MainLayout showHeader showFooter backgroundColor="white"><HeadOfSales /></MainLayout>} />

      {/* Link app */}
      <Route path="/link-app/*" element={<LinkApp />} />

      {/* Empty layout routes */}
      <Route path="/check-email" element={<EmptyLayout><CheckEmail /></EmptyLayout>} />
      <Route path="/choose-password/:token" element={<EmptyLayout><ChoosePassword /></EmptyLayout>} />
      <Route path="/new-user/update" element={<EmptyLayout><NewUserUpdate /></EmptyLayout>} />
      <Route path="/new-user/:token" element={<EmptyLayout><NewUser /></EmptyLayout>} />
      <Route path="/no-account" element={<EmptyLayout><NoAccount /></EmptyLayout>} />
      <Route path="/email-verified" element={<EmptyLayout><EmailVerified /></EmptyLayout>} />
      <Route path="/verify-email" element={<EmptyLayout><VerifyEmail /></EmptyLayout>} />
      <Route path="/logout" element={<Logout />} />

      {/* Redirects */}
      <Route path="/login-mobile" element={<Navigate to="/login" replace />} />

      {/* Fallback */}
      <Route path="*" element={<NotFound />} />
    </Route>,
  ),
);

const App: React.FC = () => (
  <PostHogProvider options={postHogOptions}
    apiKey={process.env.PUBLIC_POSTHOG_KEY ?? "" }>
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <ApolloProvider client={apolloClient}>
          <ViewerProvider>
            <SnackbarProvider>
              <TablesRefProvider>
                <NonProductionBanner />
                <TimeoutLogoutComponent />
                <RouterProvider router={router} />
              </TablesRefProvider>
            </SnackbarProvider>
          </ViewerProvider>
        </ApolloProvider>
      </LocalizationProvider>
    </ThemeProvider>
  </PostHogProvider>
);

export default App;
